/**
 * Tree of Life - Observable HQ pattern implementation
 * Organizational tree structure
 * https://observablehq.com/@d3/tree-of-life
 */

import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { Box, Paper, Typography } from '@mui/material';
import useTokenExaminerStore from '../../stores/tokenExaminerStore';
import { getFaanaHealthColor } from '../../utils/faanaScoring';
import { getContrastText } from '../../utils/colorContrast';

// eslint-disable-next-line max-lines-per-function
function TreeOfLife() {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  const {
    _relationships,
    entities,
    getFilteredRelationships,
  } = useTokenExaminerStore();

  const filteredRelationships = getFilteredRelationships();

  // Update dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  // Render tree of life
  // eslint-disable-next-line max-lines-per-function
  useEffect(() => {
    if (!dimensions.width || !dimensions.height || !filteredRelationships.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const margin = { top: 20, right: 20, bottom: 20, left: 20 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    const container = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Build hierarchical structure based on relationship strength and centrality
    const entityArray = Array.from(entities.values());

    // Calculate centrality for each entity
    const entityMetrics = entityArray.map(entity => {
      const entityRelationships = filteredRelationships.filter(r =>
        (r.sourceEntity?.crossEntityHash || r.sourceEntityHash) === entity.crossEntityHash ||
        (r.targetEntity?.crossEntityHash || r.targetEntityHash) === entity.crossEntityHash
      );

      const connectionCount = entityRelationships.length;
      const avgHealth = connectionCount > 0
        ? entityRelationships.reduce((sum, r) => sum + (r.overallHealth || 0), 0) / connectionCount
        : 0;

      // Calculate betweenness centrality (simplified)
      let centralityScore = connectionCount;

      return {
        ...entity,
        connectionCount,
        avgHealth,
        centralityScore,
        relationships: entityRelationships
      };
    }).sort((a, b) => b.centralityScore - a.centralityScore);

    // Create tree structure - most central entity as root
    const root = entityMetrics[0];
    const remaining = entityMetrics.slice(1);

    // Build tree by connecting entities to their strongest relationships
    const buildTree = (rootEntity, availableEntities, maxDepth = 4, currentDepth = 0) => {
      if (currentDepth >= maxDepth || availableEntities.length === 0) {
        return {
          name: rootEntity.name,
          id: rootEntity.crossEntityHash,
          type: rootEntity.type,
          data: rootEntity
        };
      }

      // Find entities connected to this root
      const connected = availableEntities.filter(entity => {
        return filteredRelationships.some(rel => {
          const sourceId = rel.sourceEntity?.crossEntityHash || rel.sourceEntityHash;
          const targetId = rel.targetEntity?.crossEntityHash || rel.targetEntityHash;
          return (
            (sourceId === rootEntity.crossEntityHash && targetId === entity.crossEntityHash) ||
            (targetId === rootEntity.crossEntityHash && sourceId === entity.crossEntityHash)
          );
        });
      });

      // Sort by relationship strength
      const sortedConnected = connected
        .map(entity => {
          const rel = filteredRelationships.find(rel => {
            const sourceId = rel.sourceEntity?.crossEntityHash || rel.sourceEntityHash;
            const targetId = rel.targetEntity?.crossEntityHash || rel.targetEntityHash;
            return (
              (sourceId === rootEntity.crossEntityHash && targetId === entity.crossEntityHash) ||
              (targetId === rootEntity.crossEntityHash && sourceId === entity.crossEntityHash)
            );
          });
          return { ...entity, relationshipStrength: rel?.overallHealth || 0 };
        })
        .sort((a, b) => b.relationshipStrength - a.relationshipStrength)
        .slice(0, 5); // Limit children per node

      const remainingAfterChildren = availableEntities.filter(e => !sortedConnected.includes(e));

      const children = sortedConnected.map(child =>
        buildTree(child, remainingAfterChildren, maxDepth, currentDepth + 1)
      );

      return {
        name: rootEntity.name,
        id: rootEntity.crossEntityHash,
        type: rootEntity.type,
        data: rootEntity,
        children: children.length > 0 ? children : undefined
      };
    };

    const treeData = buildTree(root, remaining);

    // Create tree layout
    const tree = d3.tree()
      .size([height, width * 0.8])
      .separation((a, b) => (a.parent === b.parent ? 1 : 1.2));

    const hierarchyRoot = d3.hierarchy(treeData);
    tree(hierarchyRoot);

    // Type colors
    const typeColors = {
      PERSON: '#2196F3',
      ORGANIZATION: '#4CAF50',
      PROGRAM: '#FF9800',
      COMMUNITY: '#9C27B0',
      UNKNOWN: '#757575'
    };

    // Draw links with organic curves
    const linkGenerator = d3.linkHorizontal()
      .x(d => d.y)
      .y(d => d.x);

    container.selectAll('.link')
      .data(hierarchyRoot.links())
      .enter().append('path')
      .attr('class', 'link')
      .attr('d', linkGenerator)
      .attr('fill', 'none')
      .attr('stroke', d => {
        // Color by relationship health if available
        const sourceId = d.source.data.id;
        const targetId = d.target.data.id;
        const rel = filteredRelationships.find(rel => {
          const relSourceId = rel.sourceEntity?.crossEntityHash || rel.sourceEntityHash;
          const relTargetId = rel.targetEntity?.crossEntityHash || rel.targetEntityHash;
          return (
            (relSourceId === sourceId && relTargetId === targetId) ||
            (relTargetId === sourceId && relSourceId === targetId)
          );
        });
        return rel ? getFaanaHealthColor(rel.overallHealth || 50) : '#999';
      })
      .attr('stroke-width', d => {
        const sourceId = d.source.data.id;
        const targetId = d.target.data.id;
        const rel = filteredRelationships.find(rel => {
          const relSourceId = rel.sourceEntity?.crossEntityHash || rel.sourceEntityHash;
          const relTargetId = rel.targetEntity?.crossEntityHash || rel.targetEntityHash;
          return (
            (relSourceId === sourceId && relTargetId === targetId) ||
            (relTargetId === sourceId && relSourceId === targetId)
          );
        });
        return rel ? Math.max(1, (rel.overallHealth || 50) / 25) : 1;
      })
      .attr('opacity', 0.6)
      .attr('stroke-linecap', 'round');

    // Draw nodes
    const nodeElements = container.selectAll('.node')
      .data(hierarchyRoot.descendants())
      .enter().append('g')
      .attr('class', 'node')
      .attr('transform', d => `translate(${d.y},${d.x})`);

    // Node circles with size based on centrality
    nodeElements.append('circle')
      .attr('r', d => {
        const baseSize = 6;
        const connectionsBonus = Math.sqrt(d.data.data?.connectionCount || 0) * 2;
        const depthPenalty = d.depth * 1;
        return Math.max(4, baseSize + connectionsBonus - depthPenalty);
      })
      .attr('fill', d => {
        const entityData = d.data.data;
        if (entityData?.avgHealth > 0) {
          return getFaanaHealthColor(entityData.avgHealth);
        }
        return typeColors[d.data.type] || typeColors.UNKNOWN;
      })
      .attr('stroke', '#fff')
      .attr('stroke-width', d => d.depth === 0 ? 3 : 2)
      .on('mouseenter', function(event, d) {
        // Highlight path to root
        const pathToRoot = [];
        let current = d;
        while (current) {
          pathToRoot.push(current.data.id);
          current = current.parent;
        }

        container.selectAll('.link')
          .attr('opacity', link => {
            const sourceInPath = pathToRoot.includes(link.source.data.id);
            const targetInPath = pathToRoot.includes(link.target.data.id);
            return (sourceInPath && targetInPath) ? 1 : 0.1;
          })
          .attr('stroke-width', link => {
            const sourceInPath = pathToRoot.includes(link.source.data.id);
            const targetInPath = pathToRoot.includes(link.target.data.id);
            if (sourceInPath && targetInPath) {
              const sourceId = link.source.data.id;
              const targetId = link.target.data.id;
              const rel = filteredRelationships.find(rel => {
                const relSourceId = rel.sourceEntity?.crossEntityHash || rel.sourceEntityHash;
                const relTargetId = rel.targetEntity?.crossEntityHash || rel.targetEntityHash;
                return (
                  (relSourceId === sourceId && relTargetId === targetId) ||
                  (relTargetId === sourceId && relSourceId === targetId)
                );
              });
              return rel ? Math.max(2, (rel.overallHealth || 50) / 15) : 2;
            }
            return Math.max(1, 1);
          });
      })
      .on('mouseleave', function() {
        container.selectAll('.link')
          .attr('opacity', 0.6)
          .attr('stroke-width', d => {
            const sourceId = d.source.data.id;
            const targetId = d.target.data.id;
            const rel = filteredRelationships.find(rel => {
              const relSourceId = rel.sourceEntity?.crossEntityHash || rel.sourceEntityHash;
              const relTargetId = rel.targetEntity?.crossEntityHash || rel.targetEntityHash;
              return (
                (relSourceId === sourceId && relTargetId === targetId) ||
                (relTargetId === sourceId && relSourceId === targetId)
              );
            });
            return rel ? Math.max(1, (rel.overallHealth || 50) / 25) : 1;
          });
      });

    // Node labels
    nodeElements.append('text')
      .attr('dy', '0.31em')
      .attr('x', d => d.children ? -10 : 10)
      .attr('text-anchor', d => d.children ? 'end' : 'start')
      .style('font-size', d => {
        const baseSize = 11;
        const depthPenalty = d.depth * 1;
        return Math.max(8, baseSize - depthPenalty) + 'px';
      })
      .style('font-weight', d => d.depth === 0 ? 'bold' : 'normal')
      .style('fill', d => {
        const entityData = d.data.data;
        let bgColor;
        if (entityData?.avgHealth > 0) {
          bgColor = getFaanaHealthColor(entityData.avgHealth);
        } else {
          bgColor = typeColors[d.data.type] || typeColors.UNKNOWN;
        }
        return getContrastText(bgColor);
      })
      .text(d => d.data.name.length > 20 ? d.data.name.substring(0, 17) + '...' : d.data.name);

    // Connection count badges
    nodeElements.filter(d => d.data.data?.connectionCount > 0)
      .append('circle')
      .attr('cx', d => d.children ? -25 : 25)
      .attr('cy', -15)
      .attr('r', 8)
      .attr('fill', '#FF5722')
      .attr('stroke', '#fff')
      .attr('stroke-width', 1);

    nodeElements.filter(d => d.data.data?.connectionCount > 0)
      .append('text')
      .attr('x', d => d.children ? -25 : 25)
      .attr('y', -11)
      .attr('text-anchor', 'middle')
      .style('font-size', '9px')
      .style('font-weight', 'bold')
      .style('fill', 'white')
      .text(d => d.data.data.connectionCount);

  }, [dimensions, filteredRelationships, entities]);

  return (
    <Box ref={containerRef} sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />

      {/* Legend */}
      <Paper
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          p: 2,
          maxWidth: 200,
        }}
        elevation={3}
      >
        <Typography variant="subtitle2" gutterBottom>
          Tree of Life
        </Typography>
        <Typography variant="caption" display="block" gutterBottom>
          • Root: Most central entity
        </Typography>
        <Typography variant="caption" display="block" gutterBottom>
          • Branches: Strong relationships
        </Typography>
        <Typography variant="caption" display="block">
          • Colors: Relationship health
        </Typography>
      </Paper>
    </Box>
  );
}

export default TreeOfLife;